import { Action, Poly } from '../features/Poly';
import { FillStyle, PolyRender } from '../renders/Poly';
import { registerSymbol } from '../serializers/symbolSerializer';
import { getRenderedPolygonCoordinates, simplifyCoordinates } from '../utils/math';
import { Symbol } from './Symbol';
/**
 * Symbol of polyline drawn as simple line.
 * @alias sGis.symbol.polyline.Simple
 */
export class PolylineSymbol extends Symbol {
    /**
     * @param options - key-value list of the properties to be assigned to the instance.
     */
    constructor(options = {}) {
        super();
        /** Stroke color of the outline. Can be any valid css color string. */
        this.strokeColor = 'black';
        /** Stroke width of the outline. */
        this.strokeWidth = 1;
        /** @see [[PolyRender.lineDash]] */
        this.lineDash = [];
        /** Emulation CanvasRenderingContext2D.filter drop-shadow. */
        this.shadow = null;
        /** @see [[PolyRender.lineCap]] */
        this.lineCap = "round";
        /** @see [[PolyRender.lineJoin]] */
        this.lineJoin = "round";
        /** @see [[PolyRender.miterLimit]] */
        this.miterLimit = 10;
        Object.assign(this, options);
    }
    renderFunction(feature, resolution, crs) {
        if (!(feature instanceof Poly))
            return [];
        let coordinates = this.getRenderedCoordinates(feature, resolution, crs);
        return [new PolyRender(coordinates, {
                fillStyle: FillStyle.None,
                enclosed: false,
                strokeColor: this.strokeColor,
                strokeWidth: this.strokeWidth,
                lineDash: this.lineDash,
                shadow: this.shadow,
                lineCap: this.lineCap,
                lineJoin: this.lineJoin,
                miterLimit: this.miterLimit,
            })];
    }
    /**
     * Projects coordinates of a poly feature to the requested crs and resolution.
     * @param feature
     * @param resolution
     * @param crs
     * @param tolerance
     */
    getRenderedCoordinates(feature, resolution, crs, tolerance) {
        let result;
        if (this.previousPoly === feature
            && this.previousCrs.equals(crs)
            && this.previousResolution === resolution
            && feature.lastAction
            && feature.lastAction.type !== Action.removePoint) {
            const lastAction = feature.lastAction;
            let newResult = this.previousSimplified;
            if (lastAction.type === Action.movePoint) {
                const newSimplifiedRing = simplifyCoordinates([feature.rings[lastAction.ringIndex]].map(ring => {
                    return ring.map(point => {
                        return [Math.round(point[0] / resolution), Math.round(point[1] / -resolution)];
                    });
                }), tolerance);
                newResult[lastAction.ringIndex] = newSimplifiedRing[0];
            }
            if (lastAction.type === Action.addPoint) {
                const renderCoordinates = [
                    Math.round(lastAction.coordinates[0] / resolution),
                    Math.round(lastAction.coordinates[1] / -resolution),
                ];
                newResult[lastAction.ringIndex].push(renderCoordinates);
            }
            result = newResult;
        }
        else {
            result = getRenderedPolygonCoordinates(feature, resolution, crs, tolerance);
        }
        this.previousSimplified = result;
        this.previousPoly = feature;
        this.previousCrs = crs;
        this.previousResolution = resolution;
        feature.lastAction = null;
        return result;
    }
}
registerSymbol(PolylineSymbol, 'polyline.Simple', ['strokeColor', 'strokeWidth', 'shadow', 'lineCap', 'lineDash', 'lineJoin', 'miterLimit']);
